import React, { useState, useEffect, forwardRef } from 'react';

import { Box } from '@chakra-ui/react';
import { Select, MenuItem } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { PlainTextHeader } from 'Deer/components/HeaderComponents/PlainTextHeader';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { Container, FormContainer, NoResultsText } from 'styles/admin';
import { Divider1, FlexContainer, VerticalFlexContainer } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { checkGoalModuleEnabled } from 'utils/helpers/moduleAccess';

import { DisplayName } from 'assets/assetsV1/iconComponents';

import { GOAL_MEASUREMENT_CURRENCIES } from 'constants/goalMeasurementCurrencies';
import { FlagIcon, CoinsIcon, ClockIcon, ChatIcon, CalendarIcon } from 'constants/icons';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { getOrganization, updateOrganization } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography4, Typography7, Typography8 } from 'uiToolkit/Typography';
import { TextLabel } from 'uiToolkitV1/Typography';

import { valuesFromSlack } from './timezone/values';

const StyledContainer = styled(Container)`
  height: auto;
`;

const StyledFormContainer = styled(FormContainer)`
  min-height: auto;
  border-radius: 8px;
  padding: 1.5rem;
  background-color: ${(props) => props.theme.palette.background.paper};
  // box-shadow: ${(props) => props.theme.palette.shadows.paper};
`;

const StyledSkeleton = styled(Skeleton)`
  width: 30%;
  height: 45px;
`;

const StyledSelect = styled(Select)`
  width: 400px;
  .MuiSelect-root {
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 16px;
  }
  fieldset {
    border: 1px solid ${(props) => props.theme.palette.borders.lighterGrey};
  }
  p {
    font-size: 14px;
  }
`;
const StyledDisplayName = styled.div`
  margin-right: 8px;
  margin-left: -2px;
`;

const styledIcon = (WrappedIcon) =>
  forwardRef((props, ref) => (
    <StyledDisplayName {...props} ref={ref}>
      <WrappedIcon />
    </StyledDisplayName>
  ));
const styledDisplayName = styledIcon(DisplayName);

const fields = [
  {
    heading: 'Country',
    subHeading: 'Please select your country',
    key: 'country',
    placeholderText: 'Enter country name',
    icon: FlagIcon,
    options: [
      { label: 'Australia', value: 'Australia' },
      { label: 'Brazil', value: 'Brazil' },
      { label: 'Canada', value: 'Canada' },
      { label: 'France', value: 'France' },
      { label: 'Germany', value: 'Germany' },
      { label: 'Hong Kong', value: 'Hong Kong ' },
      { label: 'India', value: 'India' },
      { label: 'Indonesia', value: 'Indonesia' },
      { label: 'Ireland', value: 'Ireland' },
      { label: 'Japan', value: 'Japan' },
      { label: 'Mexico', value: 'Mexico' },
      { label: 'Pakistan', value: 'Pakistan' },
      { label: 'Philippines', value: 'Philippines' },
      { label: 'Singapore', value: 'Singapore' },
      { label: 'UK', value: 'UK' },
      { label: 'USA', value: 'USA' },
    ],
  },
  {
    heading: 'Currency',
    subHeading: 'Please select your currency',
    key: 'currency',
    placeholderText: 'Enter your currency here',
    icon: CoinsIcon,
    options: Object.keys(GOAL_MEASUREMENT_CURRENCIES).map((x) => ({
      label: `${GOAL_MEASUREMENT_CURRENCIES[x].name} ` + `(${GOAL_MEASUREMENT_CURRENCIES[x].symbol})`, // eslint-disable-line no-useless-concat
      value: x,
    })),
  },
  {
    heading: 'Timezone',
    subHeading: 'Please select your timezone',
    key: 'timezone',
    placeholderText: 'Please select your timezone',
    icon: ClockIcon,
    options: valuesFromSlack,
  },
  {
    heading: 'Language',
    subHeading: 'Please select your language',
    key: 'language',
    placeholderText: 'Please select your language',
    icon: ChatIcon,
    options: [
      { label: 'English', value: 'English' },
      { label: 'Portuguese', value: 'Portuguese' },
      { label: 'French', value: 'French' },
      { label: 'German', value: 'German' },
      { label: 'Cantonese', value: 'Cantonese' },
      { label: 'Japanese', value: 'Japanese' },
      { label: 'Spanish', value: 'Spanish' },
    ],
  },
  {
    heading: 'Start Month',
    subHeading: 'Choose start month of the first cycle (Q1, H1, Y1...)',
    key: 'startMonth',
    placeholderText: '',
    icon: CalendarIcon,
    options: [
      { label: 'January', value: 'JANUARY' },
      { label: 'February', value: 'FEBRUARY' },
      { label: 'March', value: 'MARCH' },
      { label: 'April', value: 'APRIL' },
      { label: 'May', value: 'MAY' },
      { label: 'June', value: 'JUNE' },
      { label: 'July', value: 'JULY' },
      { label: 'August', value: 'AUGUST' },
      { label: 'September', value: 'SEPTEMBER' },
      { label: 'October', value: 'OCTOBER' },
      { label: 'November', value: 'NOVEMBER' },
      { label: 'December', value: 'DECEMBER' },
    ],
  },
  {
    heading: 'Display Name',
    subHeading: 'Select display preference: Preferred name (if available) or always use first name',
    key: 'displayNameConfig',
    placeholderText: '',
    icon: styledDisplayName,
    options: [
      {
        label: 'Always show First name and Last name',
        value: 'FIRST_NAME_LAST_NAME',
      },
      {
        label: 'Show Preferred name and Last name if available',
        value: 'PREFERRED_NAME_LAST_NAME',
      },
    ],
  },
];

const OrgSettingItem = ({ changeHandler, heading, description, icon: Icon, value, options, isDisabled }) => (
  <FlexContainer style={{ alignItems: 'flex-start' }}>
    <Icon size="fixed14px" color={theme.palette.icons.black2} rightGutter={2} topGutter={0.6} />
    <VerticalFlexContainer style={{ flexGrow: 1 }}>
      <Typography7>{heading}</Typography7>
      <Typography8 topGutter={2}>{description}</Typography8>
    </VerticalFlexContainer>

    <StyledSelect
      variant="outlined"
      displayEmpty
      value={value}
      onChange={({ target }) => {
        changeHandler(target.value);
      }}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        getContentAnchorEl: null,
      }}
      isDisabled={isDisabled}
    >
      {/* eslint-disable-next-line no-shadow */}
      {options.map(({ label, value }, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <MenuItem key={`source-option-${index}`} value={value}>
          <Typography4>{label}</Typography4>
        </MenuItem>
      ))}
    </StyledSelect>
  </FlexContainer>
);

/**
 * @deprecated 8-04-2025
 */
const OrgSettings = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [settings, setSettings] = useState(
    !checkGoalModuleEnabled() ? fields.filter(({ key }) => key !== 'currency') : fields
  );
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const orgId = loggedInEmployee.organization.id;

  const getData = () => {
    setLoading(true);
    getOrganization(orgId)
      .then((res) => {
        setOrganization(res);
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Unable to fetch the details.')))
      .finally(() => setLoading(false));
  };

  useEffect(
    getData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (checkNewNavEnabled()) {
      sendPageViewEvent({
        eventProperties: {
          page: 'Company-settings-organisation details',
        },
      });
    }
  }, []);

  const handleChange = (key, value) => {
    if (organization[key] === value) return;
    const org = { ...organization, [key]: value };
    setLoading(true);
    updateOrganization(org)
      .then(() => {
        getData();
      })
      .catch((err) => dispatch(showErrorSnackbar(err?.message || 'Unable to update the settings.')))
      .finally(() => setLoading(false));
  };

  return (
    <StyledContainer>
      <Box
      // pt={2}
      >
        <PlainTextHeader>Organization details</PlainTextHeader>
        <TextLabel color="text.g2" mb={2}>
          These settings will help update general preferences for your organization so we can serve you better
        </TextLabel>
      </Box>
      {organization || loading ? (
        <StyledFormContainer>
          {settings.map((setting, i) => (
            <>
              {loading ? (
                // eslint-disable-next-line react/no-array-index-key
                <StyledSkeleton key={i} />
              ) : (
                <OrgSettingItem
                  heading={setting.heading}
                  description={setting.subHeading}
                  value={setting.options.filter((opt) => opt.value === organization[setting.key])[0]?.value}
                  icon={setting.icon}
                  key={setting.key}
                  options={setting.options}
                  changeHandler={(value) => handleChange(setting.key, value)}
                  isDisabled={loading}
                />
              )}
              {/* {i < settings.length - 1 && <Divider />} */}
              {i < settings.length - 1 && <Divider1 topGutter={3} bottomGutter={3} />}
            </>
          ))}
        </StyledFormContainer>
      ) : (
        <NoResultsText fullHeight>Sorry, no details were found.</NoResultsText>
      )}
    </StyledContainer>
  );
};

export default OrgSettings;
